import React, { useEffect, useMemo, useState } from "react";
import { Menu, Box, UnstyledButton, Group, Avatar, Text, Tooltip } from "@mantine/core";
import CheckboxFilter from "../../filters/types/CheckboxFilter";
import { useFiltersContextHelper } from "../../filters/useFiltersContextHelper";
import { useIdeaFilters } from "../../../../hooks/useIdeaFilters";
import { IconChevronDown, IconUser, IconUsers } from "@tabler/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { getConnectedUserEmail } from "../../../../utils";
import { useIdeasUrlState } from "../../../../hooks/useIdeasUrlState";


const StickyOwnerFilter: React.FC = ({ subPageKey }: any) => {
  const { ideasFiltersConfig } = useIdeaFilters();
  const {
    setSelectedParentFilter,
    setCheckedComponents,
    selectedParentFilter,
    activeFilters,
  } = useFiltersContextHelper();
  const { globalFilters, setOwner } = useIdeasUrlState({ subPageKey });

  const [opened, setOpened] = useState(false);
  const auth0 = useAuth0();
  const currentUserEmail = (getConnectedUserEmail(auth0) || "").toLowerCase();

  // Close the menu if context clears the selected parent (e.g., on Apply)
  useEffect(() => {
    if (!selectedParentFilter) {
      setOpened(false);
    }
  }, [selectedParentFilter]);

  // Determine current owner selection state for trigger rendering
  const ownerState = useMemo(() => {
    // Prefer globalFilters.owner (the sticky control's own explicit selection). Fall
    // back to the merged/applied filter when the sticky control itself hasn't been
    // touched but a page/view default still applies "owner" — otherwise the trigger
    // shows "Select owner..." while the dropdown (seeded from activeFilters) and the
    // actual query both reflect an applied selection.
    const appliedFilter = activeFilters?.find(
      (f: any) => (f?.parent || f?.name)?.toLowerCase() === 'owner'
    );
    const selected: string[] =
      Array.isArray(globalFilters?.owner) && globalFilters.owner.length > 0
        ? globalFilters.owner
        : Array.isArray(appliedFilter?.selected)
          ? appliedFilter.selected
          : [];
    const count = selected.length;

    const ownerOptions: any[] = (ideasFiltersConfig as any)?.idea?.owner?.options || [];

    let firstOwner: { name?: string; label?: string; picture?: string; email?: string } | null = null;
    if (count === 1) {
      const first = String(selected[0]).toLowerCase();
      firstOwner =
        ownerOptions.find(
          (o: any) =>
            (o.value?.toLowerCase?.() || o.label?.toLowerCase?.()) === first ||
            (o.email?.toLowerCase?.()) === first
        ) || null;
    }

    // Build display names for tooltip when multiple owners
    const buildOptionsMap = (opts: any[]): Record<string, string> => {
      const map: Record<string, string> = {};
      const traverse = (list: any[]) => {
        list.forEach((o: any) => {
          if (!o) return;
          const key = String(o.value || o.label || "").toLowerCase();
          if (key) map[key] = o.label || o.name || key;
          if (Array.isArray(o.childComponents) && o.childComponents.length > 0) {
            traverse(o.childComponents);
          }
        });
      };
      traverse(opts);
      return map;
    };

    let multipleTooltip = "";
    if (count > 1 && Array.isArray(ownerOptions)) {
      const map = buildOptionsMap(ownerOptions);
      const names = selected.map((s) => map[String(s).toLowerCase()] || String(s));
      multipleTooltip = names.join(", ");
    }

    return { count, firstOwner, multipleTooltip };
  }, [globalFilters?.owner, ideasFiltersConfig, activeFilters]);

  const trigger = (
    <UnstyledButton
      aria-label="Owner filter"
      style={{
        height: 32,
        boxSizing: "border-box",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "0 8px",
        background: "#FFFFFF",
        width: 145,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      {ownerState.count === 0 && (
        <Group spacing={6} noWrap h={32}>
          <IconUser size={18} color="#6B7280" />
          <Text size="sm" c="dimmed" lh={1.2} fz={12}>
            Select owner...
          </Text>
        </Group>
      )}
      {ownerState.count === 1 && (
        <Group spacing={6} noWrap h={32}>
          <Avatar
            src={ownerState.firstOwner?.picture || undefined}
            radius="xl"
            size={22}
          />
          <Text fz={12} lh={1.2}>
            {(() => {
              const base =
                ownerState.firstOwner?.name ||
                ownerState.firstOwner?.label ||
                "Owner";
              const isMe =
                !!currentUserEmail &&
                !!ownerState.firstOwner?.email &&
                ownerState.firstOwner.email.toLowerCase() === currentUserEmail;
              return isMe ? `${base} (me)` : base;
            })()}
          </Text>
        </Group>
      )}
      {ownerState.count > 1 && (
        <Group spacing={8} noWrap h={32}>
          <Group spacing={6} noWrap>
            <IconUsers size={18} color="#6B7280" />
            <Text size="sm" lh={1.2} fz={12}>
              {ownerState.count}
            </Text>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#5C5CEB",
              }}
            />
          </Group>
          <Tooltip
            maw={600}
            multiline
            label={ownerState.multipleTooltip}
          >
            <Text fz={12} lh={1.2} lineClamp={1}>
              Multiple owners
            </Text>
          </Tooltip>
        </Group>
      )}
      <IconChevronDown size={14} color="#6B7280" />
    </UnstyledButton>
  );

  const resetFilters = (MenuType: "idea" | "evidence", selectedParentFilter?: string | null) => {
    const parentKey = selectedParentFilter ?? "owner";

    setCheckedComponents((current: any) => {
      const next: { [key: string]: any[] } = {};
      Object.keys(current).forEach((key) => {
        next[key] = key.split(".")[0] === parentKey ? [] : current[key];
      });
      return next;
    });

    if (parentKey === "owner") {
      setOwner([]);
    }
  };

  return (
    <Menu
      shadow="md"
      radius="md"
      width={260}
      position="bottom-start"
      opened={opened}
      onChange={(isOpen) => {
        setOpened(isOpen);
        if (isOpen) {
          setSelectedParentFilter("owner");
        } else {
          setSelectedParentFilter(null);
        }
      }}
    >
      <Menu.Target>
        {trigger}
      </Menu.Target>
      <Menu.Dropdown sx={{ padding: 0, paddingTop: 5 }}>
        <Box>
          <CheckboxFilter
            initialfilters={ideasFiltersConfig.idea}
            MenuType="idea"
            resetFilters={resetFilters}
            ideasFiltersConfig={ideasFiltersConfig}
            setOpened={setOpened}
            setValue={setOwner}
            hideAddButton
          />
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default StickyOwnerFilter;
